import { useEffect, useRef, useState } from 'react';
import { IconProps } from '../../DataDisplay/Icon/Icon';
import OutsideClickHandler from '../../../vendor/OutsideClickHandler';
import PopOver from '../../Utility/PopOver/PopOver';
import DropDownIndicator from '../../Atomic/DropDownIndicator/DropDownIndicator';
import DropDownMenu, { DropdownMenuItemStyle } from '../../Atomic/DropDownMenu/DropDownMenu';

import styles from './DropDown.module.scss';

export type DropDownProps = {
  variant?: 'bubble' | 'filter' | 'plain' | 'lined' | 'multi' | 'multiFilter' | 'outlined';
  label?: string | React.JSX.Element;
  icon?: IconProps;
  children?: React.ReactNode;
  isMenuVisible?: boolean;
  maxHeight?: string;
  topPosition?: string;
  indicatorMinHeight?: string;
  fontColor?: string;
  disabled?: boolean;
  width?: string;
  header?: boolean;
  dropdownStyle?: DropdownMenuItemStyle;
  menuPosition?: 'left' | 'right';
  position?: 'fixed' | 'absolute';
  onClick?: (isVisible: boolean) => void;
};

const DropDown = ({
  variant,
  label,
  icon,
  children,
  isMenuVisible = false,
  maxHeight,
  topPosition,
  indicatorMinHeight,
  fontColor,
  disabled,
  width,
  header = true,
  position = 'absolute',
  onClick = () => {},
  dropdownStyle,
}: DropDownProps): React.JSX.Element => {
  const [positionInfo, setPositionInfo] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const pickerEl = useRef<HTMLInputElement>(null);

  // Update position info when the dropdown is visible
  useEffect(() => {
    if (isMenuVisible && pickerEl.current) {
      const iconRect = pickerEl.current.getBoundingClientRect();

      setPositionInfo({
        top: iconRect.top,
        left: iconRect.left,
        width: iconRect.width,
        height: iconRect.height,
      });
    }
  }, [isMenuVisible]);

  return (
    <div style={{ width: '100%' }}>
      <div className={[styles['drop-down-container'], styles['margin-top']].join(' ')}>
        <DropDownIndicator
          variant={variant}
          fontColor={fontColor}
          disabled={disabled}
          label={label}
          icon={icon}
          header={header}
          showAngleDown={false}
          indicatorMinHeight={indicatorMinHeight}
          onClick={() => onClick(!isMenuVisible)}
          ref={pickerEl}
        />

        {/* Show PopOver when position is 'fixed' */}
        {position === 'fixed' && isMenuVisible && (
          <PopOver style={positionInfo}>
            <OutsideClickHandler onOutsideClick={() => onClick(false)}>
              <DropDownMenu
                variant={variant}
                width={width}
                header={header}
                isVisible={isMenuVisible}
                maxHeight={maxHeight}
                topPosition={topPosition}
                style={dropdownStyle}
              >
                {children}
              </DropDownMenu>
            </OutsideClickHandler>
          </PopOver>
        )}

        {/* Always show the DropDownMenu */}
        {isMenuVisible && position !== 'fixed' && (
          <OutsideClickHandler onOutsideClick={() => onClick(false)}>
            <DropDownMenu
              variant={variant}
              width={width}
              header={header}
              isVisible={isMenuVisible}
              maxHeight={maxHeight}
              topPosition={topPosition}
              style={dropdownStyle}
            >
              {children}
            </DropDownMenu>
          </OutsideClickHandler>
        )}
      </div>
    </div>
  );
};

export default DropDown;
